// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import React, { useState } from 'react';
import { Table, TableRow, TableBody, TableHead, TableCell } from '../../../../components/organisms/Table';
import { Pagination } from '../../../../_lib/components/Pagination';
import { MemberRow } from '../../../../components/molecules/MemberRow/MemberRow';
import { BlockType } from '../../../../types/api/ElementType';
import { MemberType } from '../../../../types/api/MemberType';
import { PaginationItem } from '../../../../_lib/components/Pagination/Pagination';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { asFetchableView } from '../../../../_lib/ViewFetcher';
import { MemberRowMode } from '../../../../components/molecules/MemberRow/MemberRowTypes';
import { useBlockContext } from '../../../../components/blocks/BlockContext';

type MembersManageType = {
  members: MemberType[];
  hideStatus: boolean;
  elementType?: string;
  pageItems?: PaginationItem[];
};

type StateType = {
  memberInMode: string | null;
  mode: MemberRowMode | null;
  requestFinished: boolean;
};

/**
 * Renders the Editview for multible Members
 */
function Members({ members, hideStatus = false, elementType, pageItems }: MembersManageType) {
  const { t } = useSonTranslation('Members');
  const { uuid } = useBlockContext();
  const [state, setState] = useState<StateType>({
    memberInMode: null,
    mode: null,
    requestFinished: true,
  });

  const getCaptionByElementType = () => {
    switch (elementType) {
      case BlockType.teamroommemberrequests:
        return t('captionRequest');
      default:
        return t('captionEdit');
    }
  };

  const labels = {
    nameLabel: t('labels.name'),
    roleLabel: t('labels.role'),
    joinDateLabel: t('labels.joinDate'),
    statusLabel: t('labels.status'),
    functionsLabel: t('labels.functions'),
    caption: getCaptionByElementType(),
  };

  /**
   * On Changing MemberRow's mode
   * this Handler will handle the disabling and rerendering of the other Memberrows
   * @param mode
   * @param memberInMode
   */
  const handleMemberRowModeChange = (mode: MemberRowMode, memberInMode: string) => {
    setState({ mode, memberInMode, requestFinished: false });
  };

  /**
   * Handles the RowChange Event of MembersRow and populates that it's request finished.
   * This will enable MemberRows actions
   */
  const handleRowChange = () => {
    setState({ ...state, requestFinished: true });
  };

  /**
   * Returns the current mode a given member has
   * @param member
   * @returns {*}
   */
  const getModeForMember = (member: MemberType): MemberRowMode | null => {
    return member.id === state.memberInMode ? state.mode : MemberRowMode.default;
  };

  /**
   * Returns a boolean which indicates whether the actions of an Members row are
   * currently diabled
   * @param member
   * @returns {boolean}
   */
  const actionsDisabled = (member: MemberType): boolean | null => {
    return member.id !== state.memberInMode && state.mode && !state.requestFinished;
  };

  if (members.length === 0) {
    return null;
  }

  // We have to aggregate the members into a format which can be used as Model
  // const models = membersToModels(members);

  return (
    <>
      <Table caption={labels.caption}>
        <TableHead>
          <TableRow>
            <TableCell>{labels.nameLabel}</TableCell>
            <TableCell>{labels.roleLabel}</TableCell>
            <TableCell>{labels.joinDateLabel}</TableCell>
            {!hideStatus && <TableCell>{labels.statusLabel}</TableCell>}
            <TableCell screenreaderOnly>{labels.functionsLabel}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {members.map((member: MemberType) => (
            <MemberRow
              key={`member-${member.id}`}
              member={member}
              hideStatus={hideStatus}
              mode={getModeForMember(member)}
              // Events
              onChangeMode={(mode: MemberRowMode) => handleMemberRowModeChange(mode, member.id)}
              onRequestFinished={handleRowChange}
              actionsDisabled={actionsDisabled(member)}
            />
          ))}
        </TableBody>
      </Table>
      {pageItems !== null && <Pagination parentHash={uuid} pageItems={pageItems} />}
    </>
  );
}

export const MembersManage = asFetchableView(Members);
